import { ChevronLeftIcon } from '@itsrever/design-system'
import ProgressBar from '@/layout/LayoutComponents/ProgressBar'
import { useLocation, useNavigate } from 'react-router'
import { useAppSelector } from '@/redux/hooks'
import clsx from 'clsx'
import { LanguageSelect } from '@/app/login/language-select'
import { PbBrandingPersistentLogoTypeEnum } from '@itsrever/rever-api'

interface Props {
    page:
        | 'order'
        | 'refund-method'
        | 'return-method'
        | 'confirm'
        | 'return-address'
        | 'exchange-address'
}

const TopBar: React.FC<Props> = ({ page }) => {
    const { pathname } = useLocation()

    const itemOptionsShown = pathname.includes('item-options')
    const navigate = useNavigate()
    const theme = useAppSelector(
        (store) => store.settingsApi.settings.response.branding
    )
    const group = useAppSelector(
        (store) => store.settingsApi.settings.response.ecommerce_group
    )

    const showLogo =
        theme?.logo_url &&
        theme?.persistent_logo_type === PbBrandingPersistentLogoTypeEnum.Enabled

    const isHoff = group === 'hoff'

    if (isHoff) {
        return (
            <>
                <div className="flex w-full items-center justify-between p-2 px-4 md:px-8">
                    <div className="mt-3 flex gap-10">
                        <img
                            src="//thehoffbrand.com/cdn/shop/files/LOGO_HOFF.svg?v=1676480209&amp;width=467"
                            alt=""
                            srcSet="//thehoffbrand.com/cdn/shop/files/LOGO_HOFF.svg?v=1676480209&amp;width=400 400w, //thehoffbrand.com/cdn/shop/files/LOGO_HOFF.svg?v=1676480209&amp;width=467 467w"
                            width="467"
                            height="90"
                            sizes="200px"
                            style={{
                                width: '200px',
                                maxWidth: '100%',
                                height: 'auto'
                            }}
                        />
                    </div>
                    <div className="flex gap-5">
                        <div className="hidden md:block">
                            <LanguageSelect />
                        </div>
                        <a href="https://thehoffbrand.com/account">
                            <svg
                                aria-hidden="true"
                                fill="none"
                                focusable="false"
                                width="24"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    d="M16 7.00046C16 9.22633 14.1653 11.034 11.9396 11.0005C9.74736 10.9674 8 9.193 8 7.00046C8 4.80793 9.74736 3.03354 11.9396 3.00046C14.1653 2.96687 16 4.77459 16 7.00046Z"
                                    stroke="currentColor"
                                    strokeWidth="1"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                ></path>
                                <path
                                    d="M1.98047 21V18C1.98047 15.7909 3.77133 14 5.98047 14H12H18.0192C20.2283 14 22.0192 15.7909 22.0192 18V21"
                                    stroke="currentColor"
                                    strokeWidth="1"
                                    strokeMiterlimit="10"
                                ></path>
                            </svg>
                        </a>
                        <a href="https://thehoffbrand.com/search">
                            <svg
                                aria-hidden="true"
                                fill="none"
                                focusable="false"
                                width="24"
                                // eslint-disable-next-line tailwindcss/no-custom-classname
                                className="header__nav-icon icon icon-search"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    d="M10.364 3a7.364 7.364 0 1 0 0 14.727 7.364 7.364 0 0 0 0-14.727Z"
                                    stroke="currentColor"
                                    strokeWidth="1"
                                    strokeMiterlimit="10"
                                ></path>
                                <path
                                    d="M15.857 15.858 21 21.001"
                                    stroke="currentColor"
                                    strokeWidth="1"
                                    strokeMiterlimit="10"
                                    strokeLinecap="round"
                                ></path>
                            </svg>
                        </a>
                        <a href="https://thehoffbrand.com/">
                            <svg
                                aria-hidden="true"
                                fill="none"
                                focusable="false"
                                width="24"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    d="M8 12V7C8 5.93913 8.42143 4.92172 9.17157 4.17157C9.92172 3.42143 10.9391 3 12 3C13.0609 3 14.0783 3.42143 14.8284 4.17157C15.5786 4.92172 16 5.93913 16 7V12M4 21H20C21.1046 21 22 20.1046 22 19V11C22 9.89543 21.1046 9 20 9H4C2.89543 9 2 9.89543 2 11V19C2 20.1046 2.89543 21 4 21Z"
                                    stroke="currentColor"
                                    strokeWidth="1"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                ></path>
                            </svg>
                        </a>
                    </div>
                </div>
            </>
        )
    }

    return (
        <div
            id="TopBar"
            className="sticky top-0 z-10 flex w-full border-b border-solid border-neutral-900/10 bg-white p-2 shadow-sm"
        >
            {page === 'order' && !itemOptionsShown ? null : (
                <button
                    id="back-button"
                    className={clsx(
                        'absolute left-2 flex cursor-pointer rounded-lg hover:bg-grey-5 md:size-fit'
                    )}
                    onClick={() => navigate(-1)}
                >
                    <span className="hidden p-1 md:block">
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                            className="size-8 -rotate-90"
                        >
                            <path
                                fill="currentColor"
                                d="M8.53 10.53a.75.75 0 1 1-1.06-1.06l4-4a.75.75 0 0 1 1.06 0l4 4a.75.75 0 1 1-1.06 1.06l-2.72-2.72v9.69a.75.75 0 0 1-1.5 0V7.81z"
                            />
                        </svg>
                    </span>
                    <span className="ml-0.5 block size-8 items-center justify-center p-2 md:hidden">
                        <ChevronLeftIcon />
                    </span>
                </button>
            )}

            <div className="mt-2 grow">
                {showLogo ? (
                    <div className="flex w-full items-center justify-center pb-3">
                        <div className="h-8 max-w-32">
                            <img
                                src={theme.logo_url}
                                loading="eager"
                                width={100}
                                height={28}
                                className="h-full w-auto object-contain"
                                alt={theme.title}
                            />
                        </div>
                    </div>
                ) : null}
                <ProgressBar page={page} />
            </div>
        </div>
    )
}

export default TopBar
